import type { GroupPublic } from '@repo/types/groups';
import type { MeetingPrivate, MeetingPublic } from '@repo/types/meetings';
import type { UserPublic } from '@repo/types/users';

import { useGetMeeting } from '@repo/api/meeting/useGetMeeting';
import { isParent } from '@repo/lib/user-lvl/isParent';
import { format } from 'date-fns';
import { Dot, MoreVertical, User, Users, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { LocationCardLocation } from '@/components/features/location/LocationCard';

import EventActionButtonGroup from '@/components/features/calendar/EventActionButtonGroup';
import GroupSecondaryCard from '@/components/features/groups/GroupSecondaryCard';
import LocationCard from '@/components/features/location/LocationCard';
import UserCard from '@/components/features/user/UserCard';
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupButtonMore, AvatarGroupContent, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge/Badge';
import { Button } from '@/components/ui/button/Button';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useMediaQuery } from '@/hooks/media-query';
import { useCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

import { EventChildren } from './EventChildren';

interface EventMoreInfoModalProps {
  event?: MeetingPublic & {
    childIds?: number[];
    location?: LocationCardLocation | null;
  };
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOpenEdit: (value: boolean) => void;
  onOpenDelete: (value: boolean) => void;
  onOpenCancel: (value: boolean) => void;
}

export default function EventMoreInfoModal(eventMoreInfoModalProps: EventMoreInfoModalProps) {
  const { event, open, onOpenChange, onOpenEdit, onOpenDelete, onOpenCancel } = eventMoreInfoModalProps;
  const breakpoint = useMediaQuery('sm');
  const { lvl, accountType } = useCredentials();
  const viewerIsParent = isParent(accountType);
  // The private payload carries the meeting's attendance and members — other
  // people's children — so it is not fetched for a parent at all.
  const { data: eventPrivate, isPending, isSuccess } = useGetMeeting(event?.id, open && !viewerIsParent);

  if (breakpoint) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="max-w-86">
          <SheetBody>
            <SheetHeader className="border-border-primary border-b">
              <SheetTitle>{event?.name}</SheetTitle>
            </SheetHeader>
            <SheetDescription className={'sr-only'}>Opisz spotkania</SheetDescription>
            <EventMoreInfoModalContent isPending={!viewerIsParent && isPending} isSuccess={isSuccess} event={event} eventPrivate={eventPrivate} hideMembers={viewerIsParent} />
          </SheetBody>
          <SheetFooter className="border-border-primary border-t">
            {!viewerIsParent && (
              <Link to={`/calendar/meeting/${event?.id}`} className="mr-auto">
                <Button size="sm" variant="outline">
                  <MoreVertical />
                  Więcej
                </Button>
              </Link>
            )}
            {lvl > 0 && (
              <EventActionButtonGroup
                onEdit={() => {
                  onOpenEdit(true);
                  onOpenChange(false);
                }}
                onDelete={() => {
                  onOpenDelete(true);
                  onOpenChange(false);
                }}
                onCancel={() => {
                  onOpenCancel(true);
                  onOpenChange(false);
                }}
                isCancelled={event?.isCancelled ?? false}
                className={'ml-auto'}
              />
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerBody>
          <DrawerHeader className="border-border-primary border-b">
            <DrawerTitle>{event?.name}</DrawerTitle>
          </DrawerHeader>
          <DrawerDescription className={'sr-only'}>Opisz spotkania</DrawerDescription>
          <EventMoreInfoModalContent isPending={!viewerIsParent && isPending} isSuccess={isSuccess} event={event} eventPrivate={eventPrivate} hideMembers={viewerIsParent} />
        </DrawerBody>
        <DrawerFooter className="border-border-primary border-t">
          {!viewerIsParent && (
            <Link to={`/calendar/meeting/${event?.id}`} className="mr-auto">
              <Button size="sm" variant="outline">
                <MoreVertical />
                Więcej
              </Button>
            </Link>
          )}
          {lvl > 0 && (
            <EventActionButtonGroup
              onEdit={() => onOpenEdit(true)}
              onDelete={() => onOpenDelete(true)}
              onCancel={() => onOpenCancel(true)}
              isCancelled={event?.isCancelled ?? false}
              className={'ml-auto'}
            />
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

interface EventMoreInfoModalContentProps {
  event?: MeetingPublic & {
    childIds?: number[];
    location?: LocationCardLocation | null;
  };
  eventPrivate?: MeetingPrivate;
  isPending: boolean;
  isSuccess: boolean;
  /** Withholds the attendance and group lists, which name other students. */
  hideMembers?: boolean;
}

const VISIBLE_USERS_LIMIT = 5;
const VISIBLE_GROUPS_LIMIT = 5;

function EventMoreInfoModalContent(eventMoreInfoModalContentProps: EventMoreInfoModalContentProps) {
  const { isPending, isSuccess, event, eventPrivate, hideMembers = false } = eventMoreInfoModalContentProps;

  const { startDate, endDate, type, desc, isCancelled, meetingURL } = event || {};

  const [openUsersDialog, setOpenUsersDialog] = useState<boolean>(false);
  const [openGroupsDialog, setOpenGroupsDialog] = useState<boolean>(false);

  const formattedDate = startDate ? format(new Date(startDate), 'dd MMM yyyy') : null;
  const startTime = startDate ? format(new Date(startDate), 'H:mm') : null;
  const endTime = endDate ? format(new Date(endDate), 'H:mm') : null;
  const meetingType = type === 'remote' ? 'Zdalnie' : 'Stacjonarnie';

  // A parent gets no private payload, but their calendar carries the location
  // on the event itself — a stationary lesson still has to say where it is.
  const location = eventPrivate?.location ?? event?.location ?? null;
  const users = eventPrivate?.attendances.map((attendance) => attendance.user) ?? [];
  const groups = eventPrivate?.groups ?? [];

  const visibleUsers = useMemo<Array<UserPublic>>(() => users.slice(0, VISIBLE_USERS_LIMIT), [users]);
  const extraUsersCount = users.length > VISIBLE_USERS_LIMIT ? users.length - visibleUsers.length : 0;
  const visibleGroups = useMemo<Array<GroupPublic>>(() => groups.slice(0, VISIBLE_GROUPS_LIMIT), [groups]);
  const extraGroupsCount = groups && groups.length > VISIBLE_GROUPS_LIMIT ? groups.length - visibleGroups.length : 0;

  return (
    <div className="shrink grow space-y-6 overflow-y-auto p-4">
      <div className="flex flex-col gap-1">
        {/* Date and time */}
        <InfoEventMoreInfoModalInfoItem label="Data">
          <p className="text-accent-foreground flex items-center truncate whitespace-nowrap">
            {formattedDate}
            <Dot className="stroke-accent-foreground size-4" />
            {startTime}-{endTime}
          </p>
        </InfoEventMoreInfoModalInfoItem>

        {/* Meeting type */}
        <InfoEventMoreInfoModalInfoItem label="Typ">
          <div>
            <Badge variant="muted">{meetingType}</Badge>
          </div>
        </InfoEventMoreInfoModalInfoItem>

        {/* Loading state */}
        {isPending && (
          <>
            <InfoEventMoreInfoModalInfoItemSkeleton />
            <InfoEventMoreInfoModalInfoItemSkeleton />
          </>
        )}

        {/* Cancelled status */}
        <InfoEventMoreInfoModalInfoItem label="Status">
          <Badge size={'sm'} variant={isCancelled ? 'destructive' : 'success'}>
            {isCancelled ? 'Odwołano' : 'Aktywne'}
          </Badge>
        </InfoEventMoreInfoModalInfoItem>
        {/* Meeting URL if available */}
        {meetingURL && (
          <InfoEventMoreInfoModalInfoItem label="Link">
            <Link to={meetingURL} target={'_blank'}>
              <Button variant={'linkSecondary'} size={'sm'}>
                <Video />
                Link do spotkania
              </Button>
            </Link>
          </InfoEventMoreInfoModalInfoItem>
        )}
        {/* Groups if available */}
        {/* Whose lesson this is, on a parent's merged calendar. */}
        {event?.childIds?.length ? (
          <InfoEventMoreInfoModalInfoItem label="Dziecko">
            <EventChildren childIds={event.childIds} variant={'names'} />
          </InfoEventMoreInfoModalInfoItem>
        ) : null}

        {!hideMembers && isSuccess && groups && groups.length > 0 && (
          <>
            <InfoEventMoreInfoModalInfoItem label="Grupy">
              <AvatarGroup>
                <AvatarGroupContent>
                  {visibleGroups.map(({ id, name, avatarURL }) => (
                    <Avatar key={id} size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={name} />
                      <AvatarFallback>
                        <Users />
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {extraGroupsCount > 0 && (
                    <AvatarGroupButtonMore type="button" onClick={() => setOpenGroupsDialog(true)}>
                      +{extraGroupsCount}
                    </AvatarGroupButtonMore>
                  )}
                </AvatarGroupContent>
              </AvatarGroup>
            </InfoEventMoreInfoModalInfoItem>
            <Dialog open={openGroupsDialog} onOpenChange={setOpenGroupsDialog}>
              <DialogContent className={'md:max-w-96'}>
                <DialogBody>
                  <DialogHeader className={'border-border-primary bg-card border-b'}>
                    <DialogTitle>Grupy spotkania</DialogTitle>
                  </DialogHeader>
                  <div className={'flex max-h-[70vh] flex-col overflow-auto p-4'}>
                    {groups.map((group) => (
                      <GroupSecondaryCard key={group.id} group={group} />
                    ))}
                  </div>
                </DialogBody>
              </DialogContent>
            </Dialog>
          </>
        )}
        {/* Users if available */}
        {!hideMembers && isSuccess && users.length > 0 && (
          <>
            <InfoEventMoreInfoModalInfoItem label="Uczestnicy">
              <AvatarGroup>
                <AvatarGroupContent>
                  {visibleUsers.map(({ id, name, surname, avatarURL }) => (
                    <Avatar key={id} size={'xs'}>
                      <AvatarImage src={getAvatar(avatarURL)} alt={`${name} ${surname}`} />
                      <AvatarFallback>
                        <User />
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {extraUsersCount > 0 && (
                    <AvatarGroupButtonMore type="button" onClick={() => setOpenUsersDialog(true)} className="h-4.5 text-xs">
                      +{extraUsersCount}
                    </AvatarGroupButtonMore>
                  )}
                </AvatarGroupContent>
              </AvatarGroup>
            </InfoEventMoreInfoModalInfoItem>
            <Dialog open={openUsersDialog} onOpenChange={setOpenUsersDialog}>
              <DialogContent className={'md:max-w-96'}>
                <DialogBody>
                  <DialogHeader className={'border-border-primary bg-card border-b'}>
                    <DialogTitle>Uczestnicy spotkania</DialogTitle>
                  </DialogHeader>
                  <div className={'flex max-h-[70vh] flex-col overflow-auto p-4'}>
                    {users.map((user) => {
                      return <UserCard key={user.id} user={user} />;
                    })}
                  </div>
                </DialogBody>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Location if available */}
        {location && (
          <InfoEventMoreInfoModalInfoItem label="Lokacja">
            <p className="text-accent-foreground">{location.name}</p>
          </InfoEventMoreInfoModalInfoItem>
        )}
        {location && <LocationCard location={location} className={'h-58'} staticMap />}
      </div>

      {/* Meeting description */}
      {desc && (
        <div className="flex flex-col gap-2">
          <h5 className="text-lg font-medium">Opis</h5>
          <p className="text-accent-foreground">{desc}</p>
        </div>
      )}
    </div>
  );
}

interface InfoEventMoreInfoModalInfoItemProps {
  label: string;
  children: React.ReactNode;
}

function InfoEventMoreInfoModalInfoItem(infoEventMoreInfoModalInfoItemProps: InfoEventMoreInfoModalInfoItemProps) {
  const { label, children } = infoEventMoreInfoModalInfoItemProps;
  return (
    <div className="flex min-h-8 items-center gap-3">
      <span className="text-muted-foreground min-w-16 text-sm">{label}:</span>
      {children}
    </div>
  );
}

function InfoEventMoreInfoModalInfoItemSkeleton() {
  return (
    <div className="flex min-h-11 max-w-[35ch] items-center gap-3">
      <Skeleton className="h-4 w-[10ch]" />
      <Skeleton className="h-4 w-[35ch]" />
    </div>
  );
}
